import type { RelationshipLabel } from './types';

/**
 * All Active Directory relationship types from tabularium
 * Source: modules/tabularium/pkg/model/model/ad_relationship.go (lines 8-155)
 *
 * Manually synced from ADRelationshipLabels array (lines 157-243)
 * Contains exactly 85 AD relationship types.
 */
const AD_RELATIONSHIP_TYPES = [
  // Ownership and Control
  'Owns',
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',

  // Group Membership
  'MemberOf',

  // Password Control
  'ForceChangePassword',

  // Extended Rights
  'AllExtendedRights',
  'AddMember',

  // Sessions and Access
  'HasSession',

  // Container Relationships
  'Contains',

  // GPO Relationships
  'GPLink',

  // Delegation
  'AllowedToDelegate',
  'CoerceToTGT',

  // Replication Rights
  'GetChanges',
  'GetChangesAll',
  'GetChangesInFilteredSet',

  // Trust Relationships
  'CrossForestTrust',
  'SameForestTrust',
  'SpoofSIDHistory',
  'AbuseTGTDelegation',

  // Resource-Based Constrained Delegation
  'AllowedToAct',

  // Administrative Access
  'AdminTo',
  'CanPSRemote',
  'CanRDP',
  'ExecuteDCOM',

  // SID History
  'HasSIDHistory',

  // Self Rights
  'AddSelf',

  // DCSync
  'DCSync',

  // Password Reading
  'ReadLAPSPassword',
  'ReadGMSAPassword',
  'DumpSMSAPassword',

  // SQL
  'SQLAdmin',

  // Specific Write Rights
  'AddAllowedToAct',
  'WriteSPN',
  'AddKeyCredentialLink',

  // Local Group Membership
  'LocalToComputer',
  'MemberOfLocalGroup',
  'RemoteInteractiveLogonRight',

  // LAPS
  'SyncLAPSPassword',

  // Write Permissions
  'WriteAccountRestrictions',
  'WriteGPLink',

  // Certificate Authority Relationships
  'RootCAFor',
  'DCFor',
  'PublishedTo',
  'ManageCertificates',
  'ManageCA',
  'DelegatedEnrollmentAgent',
  'Enroll',
  'HostsCAService',
  'WritePKIEnrollmentFlag',
  'WritePKINameFlag',
  'NTAuthStoreFor',
  'TrustedForNTAuth',
  'EnterpriseCAFor',
  'IssuedSignedBy',
  'GoldenCert',
  'EnrollOnBehalfOf',

  // Certificate Template Links
  'OIDGroupLink',
  'ExtendedByPolicy',

  // ADCS Attack Paths
  'ADCSESC1',
  'ADCSESC3',
  'ADCSESC4',
  'ADCSESC6a',
  'ADCSESC6b',
  'ADCSESC9a',
  'ADCSESC9b',
  'ADCSESC10a',
  'ADCSESC10b',
  'ADCSESC13',

  // Azure Sync
  'SyncedToEntraUser',

  // NTLM Coercion and Relay
  'CoerceAndRelayNTLMToSMB',
  'CoerceAndRelayNTLMToADCS',
  'CoerceAndRelayNTLMToLDAP',
  'CoerceAndRelayNTLMToLDAPS',

  // Limited Rights Variants
  'WriteOwnerLimitedRights',
  'WriteOwnerRaw',
  'OwnsLimitedRights',
  'OwnsRaw',

  // Special Identity
  'ClaimSpecialIdentity',

  // Identity and ACE Propagation
  'ContainsIdentity',
  'PropagatesACEsTo',

  // GPO Application
  'GPOAppliesTo',
  'CanApplyGPO',

  // Trust Keys
  'HasTrustKeys',
] as const;

/**
 * Convert AD relationship type to display-friendly format
 * Handles consecutive capitals (acronyms) correctly
 *
 * Rules:
 * - lowercase → Uppercase: add space between (e.g., "memberOf" → "member Of")
 * - Uppercase → lowercase: add space before uppercase (e.g., "TGTDelegation" → "TGT Delegation")
 * - Consecutive capitals stay together (e.g., "TGT" stays as "TGT", not "T G T")
 *
 * @example
 * formatADRelationshipLabel("MemberOf") → "Member Of"
 * formatADRelationshipLabel("DCSync") → "DC Sync"
 * formatADRelationshipLabel("ReadLAPSPassword") → "Read LAPS Password"
 * formatADRelationshipLabel("AbuseTGTDelegation") → "Abuse TGT Delegation"
 * formatADRelationshipLabel("ADCSESC1") → "ADCSESC 1"
 */
function formatADRelationshipLabel(actual: string): string {
  return (
    actual
      // Add space between lowercase and uppercase
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Add space before uppercase letter when followed by lowercase (handles acronyms)
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
  );
}

/**
 * All AD relationship labels with display/actual distinction
 * Used by AD_RELATIONSHIPS definition in definitions.ts
 *
 * Total count: 85 relationships
 */
export const AD_RELATIONSHIP_LABELS: RelationshipLabel[] =
  AD_RELATIONSHIP_TYPES.map(type => ({
    actual: type,
    display: formatADRelationshipLabel(type),
  }));
